/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Dashboard tab definitions, which tiers disable which tab, and URL → tab matching.
 */

import { ROUTES } from './routes';

export const DASHBOARD_TAB_DISABLE_RULES = {
    Dashboard: ['Default', 'CoPilot'],
    MileMaker: ['Default', 'CoPilot', 'Silver', 'Gold'],
    TopRates: ['Default', 'CoPilot', 'Silver'],
    RatesAndHistory: ['Default'],
    Insights: ['Default', 'Silver', 'Gold', 'CoPilot'],
    RFQ: ['Default', 'Silver', 'CoPilot'],
    Administration: ['Default'],
    News: ['Default'],
};

export const DASHBOARD_TABS = [
    {
        id: 'home',
        path: ROUTES.DASHBOARD_HOME,
        disableKey: 'Dashboard',
        label: 'Dashboard',
    },
    {
        id: 'milemaker',
        path: ROUTES.DASHBOARD_MILEMAKER,
        disableKey: 'MileMaker',
        label: 'MileMaker',
        tooltip: 'Platinum Feature only',
    },
    {
        id: 'search',
        path: ROUTES.DASHBOARD_SEARCH,
        disableKey: 'RatesAndHistory',
        label: 'Rates & History',
    },
    {
        id: 'top-rates',
        path: ROUTES.DASHBOARD_TOP_RATES,
        disableKey: 'TopRates',
        label: 'Top Rates',
        panelClassName: 'v2-tab-content',
    },
    {
        id: 'insights',
        path: ROUTES.DASHBOARD_INSIGHTS,
        disableKey: 'Insights',
        label: 'Insights',
        tooltip: 'Platinum Feature',
    },
    {
        id: 'rfq',
        path: ROUTES.DASHBOARD_RFQ,
        disableKey: 'RFQ',
        label: 'RFQ',
        tooltip: 'Gold & Platinum Feature',
    },
    {
        id: 'profile',
        path: ROUTES.DASHBOARD_PROFILE,
        disableKey: 'Setup',
        label: 'Setup',
    },
    {
        id: 'admin',
        path: ROUTES.DASHBOARD_ADMIN,
        disableKey: 'Administration',
        label: 'Administration',
        adminOnly: true,
    },
    {
        id: 'news',
        path: ROUTES.DASHBOARD_NEWS,
        disableKey: 'News',
        label: 'News',
    },
];

export function normalizeDashboardPath(pathname) {
    if (!pathname) {
        return ROUTES.DASHBOARD;
    }
    const trimmed = pathname.replace(/\/+$/, '');
    return trimmed || ROUTES.DASHBOARD;
}

export function isDashboardTabDisabled(disableKey, subscriptionType) {
    return Boolean(DASHBOARD_TAB_DISABLE_RULES[disableKey]?.includes(subscriptionType));
}

export function getVisibleDashboardTabs(isAdmin) {
    return DASHBOARD_TABS.filter((tab) => !tab.adminOnly || isAdmin);
}

export function matchDashboardTabId(pathname) {
    const normalized = normalizeDashboardPath(pathname);
    const ranked = [...DASHBOARD_TABS].sort((a, b) => b.path.length - a.path.length);
    for (const tab of ranked) {
        if (tab.path === ROUTES.DASHBOARD) {
            if (normalized === ROUTES.DASHBOARD) {
                return tab.id;
            }
            continue;
        }
        if (normalized === tab.path || normalized.startsWith(`${tab.path}/`)) {
            return tab.id;
        }
    }
    return null;
}

export function getDefaultDashboardPath(subscriptionType) {
    if (subscriptionType === 'CoPilot') {
        return ROUTES.DASHBOARD_SEARCH;
    }
    return ROUTES.DASHBOARD;
}

export function resolveDashboardLocation({
    pathname,
    search = '',
    subscriptionType,
    isAdmin = false,
}) {
    const params = new URLSearchParams(search);
    if (params.get('tab') === 'setup') {
        return { redirectTo: ROUTES.DASHBOARD_PROFILE };
    }

    const defaultPath = getDefaultDashboardPath(subscriptionType);
    const tabId = matchDashboardTabId(pathname);
    if (!tabId) {
        return { redirectTo: defaultPath };
    }

    const tab = DASHBOARD_TABS.find((item) => item.id === tabId);
    if (tab.adminOnly && !isAdmin) {
        return { redirectTo: defaultPath };
    }
    if (isDashboardTabDisabled(tab.disableKey, subscriptionType)) {
        return { redirectTo: defaultPath };
    }

    return { tabId };
}
